import { useEffect } from 'react'

import { useActiveRecording } from '@/apps/latest/shared/contexts/ActiveRecordingContext'

import type { RecordingViewModel } from './types'

/**
 * page が録音 hook を持つときに 1 行で呼ぶ emit hook.
 *
 * 旧版では MeetingBlock 内部の useEffect 2 本が emitter 責務を持っていたが、
 * MeetingBlock 不在 page (= recording-new の start 前画面など) で emit が外れる問題があった.
 * page-level に hoist することで、 MeetingBlock の mount/unmount に依らず emit が持続する.
 *
 * isNavBlocked = viewStatus in {starting, recording, stopping} (= 録音 hardware lifecycle 進行中).
 * generating (= server SOAP 生成中) は画面離脱しても server 処理が継続するため block しない.
 *
 * cancel handler (=「中断して移動」) は vm.discardRecording を登録する: 破棄の入口は 1 本
 * (ADR-0039)。移動時にも「録音を破棄しました」の toast が出るようになる。
 */
export const useActiveRecordingEmit = (vm: RecordingViewModel): void => {
  const { setActiveRecording, registerCancelHandler, unregisterCancelHandler } =
    useActiveRecording()

  useEffect(() => {
    setActiveRecording(vm.isNavBlocked)
    return () => setActiveRecording(false)
  }, [vm.isNavBlocked, setActiveRecording])

  // engine.cancel() を直呼びすると楽観更新 / invalidate / toast が付かない (JSDoc 参照).
  const { discardRecording } = vm
  useEffect(() => {
    registerCancelHandler(() => void discardRecording())
    return () => unregisterCancelHandler()
  }, [discardRecording, registerCancelHandler, unregisterCancelHandler])
}
